import { useState, useRef } from "react";
import axios from "axios";
import { EyeIcon, EyeCloseIcon, CheckMail, AuthLock } from "asset/icons";
import Loader from "component/loader";
import { useNavigate } from "react-router-dom";
import ErrorMessage from "./ErrorMessage";

const PinSetup = () => {
    const [pin, setPin] = useState(["", "", "", ""]);
    const [showPin, setShowPin] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const inputRefs = useRef([]);
    const navigate = useNavigate();

    const handleChange = (index, value) => {
        if (!/^[0-9]?$/.test(value)) return;
        const updatedPin = [...pin];
        updatedPin[index] = value;
        setPin(updatedPin);
        if (value && index < 3) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !pin[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleSubmit = async () => {
        const pinCode = pin.join("");
        setLoading(true);
        setError("");

        try {
            await axios.post("/v1/users/create-pin", { pin: pinCode });
            navigate("/auth/activate-account");
        } catch (err) {
            const msg = err?.response?.data?.message || "An error occurred";
            if (msg === "PIN code already created") {
                setError(msg);
                setTimeout(() => {
                    navigate("/dashboard");
                }, 2000);
            } else {
                setError(msg);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl w-[600px] border border-[#EBEDF9] mx-auto">
            <div className="flex !pt-5">
                <AuthLock />
            </div>
            <div className="p-8 !pt-0">
                <h2 className="text-2xl font-bold text-gray-900">Set up a payment PIN</h2>
                <p className="text-sm text-gray-500 mb-6">
                    Create a 4-digit code to confirm your transactions and important actions.
                </p>

                {/* PIN Inputs */}
                <div className="flex gap-3 my-6">
                    {pin.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type={showPin ? "text" : "password"}
                            maxLength="1"
                            inputMode="numeric"
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className="w-12 h-12 text-2xl text-center border border-gray-300 rounded-md focus:border-[#6F00FF] focus:ring-2 focus:ring-[#6F00FF] transition"
                        />
                    ))}
                    <button
                        type="button"
                        onClick={() => setShowPin(!showPin)}
                        className="flex items-center"
                    >
                        {showPin ? (
                            <EyeCloseIcon className="w-5 h-5 text-gray-500" />
                        ) : (
                            <EyeIcon className="w-5 h-5 text-gray-500" />
                        )}
                    </button>
                </div>

                {/* Error message */}
                <ErrorMessage message={error} />

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        onClick={handleSubmit}
                        disabled={pin.some((p) => p === "") || loading}
                        className={`py-2 px-6 rounded-lg text-white text-sm font-semibold transition ${
                            pin.every((p) => p !== "") && !loading
                                ? "bg-[#6F00FF] hover:bg-[#5800CC]"
                                : "bg-[#E9D7FE] cursor-not-allowed"
                        }`}
                    >
                        {loading ? <Loader /> : "Continue"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PinSetup;
